import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, X, Trash2, Phone, Mail, MapPin, Calendar, Users, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { QUOTE_STATUSES, statusMeta } from "@/lib/data";

function StatusBadge({ status }) {
  const m = statusMeta(status);
  return <span className="inline-block rounded-full px-2.5 py-1 text-xs font-medium text-white" style={{ backgroundColor: m.color }}>{m.label}</span>;
}

export default function AdminDemandes() {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/quotes", { params: { status: filter, q } })
      .then((r) => setQuotes(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
    api.get("/quotes/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  const updateQuote = async (id, patch) => {
    try {
      const r = await api.patch(`/quotes/${id}`, patch);
      setSelected(r.data);
      toast.success("Demande mise à jour");
      load();
    } catch { toast.error("Échec de la mise à jour"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try { await api.delete(`/quotes/${id}`); setSelected(null); toast.success("Demande supprimée"); load(); }
    catch { toast.error("Échec de la suppression"); }
  };

  return (
    <div data-testid="admin-demandes">
      <h1 className="font-serif text-3xl text-[#2C2C2C]">Demandes de devis</h1>
      <p className="mt-1 text-sm text-[#2C2C2C]/60">{stats.total} demande(s) au total</p>

      {/* Stats row */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <button onClick={() => setFilter("all")} className={`rounded-xl border p-3 text-left ${filter === "all" ? "border-[#2C2C2C] bg-white" : "border-[#2C2C2C]/10 bg-white"}`} data-testid="stat-all">
          <p className="text-2xl font-600 text-[#2C2C2C]">{stats.total}</p>
          <p className="text-xs text-[#2C2C2C]/60">Toutes</p>
        </button>
        {QUOTE_STATUSES.map((s) => (
          <button key={s.value} onClick={() => setFilter(s.value)} className={`rounded-xl border p-3 text-left ${filter === s.value ? "border-[#2C2C2C]" : "border-[#2C2C2C]/10"} bg-white`} data-testid={`stat-${s.value}`}>
            <p className="text-2xl font-600" style={{ color: s.color }}>{stats.by_status?.[s.value] || 0}</p>
            <p className="text-xs leading-tight text-[#2C2C2C]/60">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mt-6 flex items-center gap-2 rounded-lg border border-[#2C2C2C]/15 bg-white px-3 py-2">
        <Search size={16} className="text-[#2C2C2C]/40" />
        <input data-testid="quote-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom, e-mail, lieu, référence…" className="w-full bg-transparent text-sm outline-none" />
      </div>

      {/* Table */}
      <div className="mt-4 overflow-hidden rounded-xl border border-[#2C2C2C]/10 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[#2C2C2C]/5 text-left text-xs uppercase tracking-wide text-[#2C2C2C]/60">
            <tr>
              <th className="px-4 py-3">Référence</th>
              <th className="px-4 py-3">Prospect</th>
              <th className="px-4 py-3">Événement</th>
              <th className="px-4 py-3">Date / Lieu</th>
              <th className="px-4 py-3">Statut</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[#2C2C2C]/50">Chargement…</td></tr>
            ) : quotes.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-[#2C2C2C]/50" data-testid="quotes-empty">Aucune demande.</td></tr>
            ) : quotes.map((qt) => (
              <tr key={qt.id} onClick={() => setSelected(qt)} data-testid={`quote-row-${qt.reference}`} className="cursor-pointer border-t border-[#2C2C2C]/5 hover:bg-[#FAF6EF]">
                <td className="px-4 py-3 font-mono text-xs text-[#2C2C2C]/70">{qt.reference}</td>
                <td className="px-4 py-3">{qt.first_name} {qt.last_name}</td>
                <td className="px-4 py-3 text-[#2C2C2C]/70">{qt.event_type}<br /><span className="text-xs text-[#C9A84C]">{qt.prestation}</span></td>
                <td className="px-4 py-3 text-[#2C2C2C]/70">{qt.event_date}<br /><span className="text-xs">{qt.event_location}</span></td>
                <td className="px-4 py-3"><StatusBadge status={qt.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-[80] flex justify-end bg-black/40" onClick={() => setSelected(null)} data-testid="quote-detail">
          <div className="h-full w-full max-w-lg overflow-y-auto bg-[#FAF6EF] p-7 thin-scroll" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-xs text-[#2C2C2C]/50">{selected.reference}</p>
                <h2 className="font-serif text-2xl text-[#2C2C2C]">{selected.first_name} {selected.last_name}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-[#2C2C2C]/50 hover:text-[#2C2C2C]"><X size={22} /></button>
            </div>

            <div className="mt-5 space-y-2 rounded-xl border border-[#2C2C2C]/10 bg-white p-4 text-sm">
              <p className="flex items-center gap-2"><Mail size={14} className="text-[#C9A84C]" /> <a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></p>
              <p className="flex items-center gap-2"><Phone size={14} className="text-[#C9A84C]" /> <a href={`tel:${selected.phone}`} className="hover:underline">{selected.phone}</a></p>
              <p className="flex items-center gap-2"><Calendar size={14} className="text-[#C9A84C]" /> {selected.event_date}</p>
              <p className="flex items-center gap-2"><MapPin size={14} className="text-[#C9A84C]" /> {selected.event_location}</p>
              <p className="flex items-center gap-2"><Users size={14} className="text-[#C9A84C]" /> {selected.guests} personnes</p>
              <p className="flex items-center gap-2"><Wallet size={14} className="text-[#C9A84C]" /> Budget : {selected.budget}</p>
            </div>

            <div className="mt-4 space-y-1 rounded-xl border border-[#2C2C2C]/10 bg-white p-4 text-sm text-[#2C2C2C]/80">
              <p><strong>Type :</strong> {selected.event_type}</p>
              <p><strong>Prestation :</strong> {selected.prestation}</p>
              {selected.format_traiteur && <p><strong>Format :</strong> {selected.format_traiteur}</p>}
              {selected.cuisine && <p><strong>Cuisine :</strong> {selected.cuisine}</p>}
              {selected.coordination_type && <p><strong>Coordination :</strong> {selected.coordination_type}</p>}
              {selected.chef_occasion && <p><strong>Occasion :</strong> {selected.chef_occasion}</p>}
              {selected.chef_format && <p><strong>Format du repas :</strong> {selected.chef_format}</p>}
              {selected.chef_services?.length > 0 && <p><strong>Prestations complémentaires :</strong> {selected.chef_services.join(", ")}</p>}
              {selected.notification && <p className="text-xs text-[#2C2C2C]/55" data-testid="quote-notification-status"><strong>Notification e-mail :</strong> {selected.notification.status}{selected.notification.to ? ` (${selected.notification.to})` : ""}</p>}
              {selected.dietary && <p><strong>Préférences :</strong> {selected.dietary}</p>}
              {selected.message && <p className="mt-2 border-t border-[#2C2C2C]/10 pt-2"><strong>Message :</strong> {selected.message}</p>}
            </div>

            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">Statut</label>
              <select data-testid="quote-status-select" value={selected.status} onChange={(e) => updateQuote(selected.id, { status: e.target.value })} className="w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-3 py-2.5 text-sm">
                {QUOTE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>

            <div className="mt-4">
              <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">Notes internes (privées)</label>
              <textarea data-testid="quote-notes" defaultValue={selected.internal_notes} rows={4} onBlur={(e) => { if (e.target.value !== selected.internal_notes) updateQuote(selected.id, { internal_notes: e.target.value }); }} className="w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-3 py-2.5 text-sm" placeholder="Visibles uniquement en interne…" />
            </div>

            <button onClick={() => remove(selected.id)} data-testid="quote-delete" className="mt-5 flex items-center gap-2 text-sm text-red-700 hover:underline"><Trash2 size={15} /> Supprimer cette demande</button>
          </div>
        </div>
      )}
    </div>
  );
}
